import {
  useInterval,
  useWindowWidth,
  useComponentVisible,
  useClickOutside,
} from "./hooks";
import { shareModal } from "./share";
import { renderParagraph, renderImage } from "./render";
import { breakpointsInt } from "themes/breakpoints.theme";

function IsOnMobile() {
  return useWindowWidth() < breakpointsInt.sm;
}

function isTouchDevice() {
  let hasTouchScreen = false;
  if ("maxTouchPoints" in navigator) {
    hasTouchScreen = navigator.maxTouchPoints > 0;
  } else if ("msMaxTouchPoints" in navigator) {
    hasTouchScreen = navigator.msMaxTouchPoints > 0;
  } else {
    var mQ = window.matchMedia && matchMedia("(pointer:coarse)");
    if (mQ && mQ.media === "(pointer:coarse)") {
      hasTouchScreen = !!mQ.matches;
    } else if ("orientation" in window) {
      hasTouchScreen = true; // deprecated, but good fallback
    } else {
      // Only as a last resort, fall back to user agent sniffing
      let UA = navigator.userAgent;
      hasTouchScreen =
        /\b(BlackBerry|webOS|iPhone|IEMobile)\b/i.test(UA) ||
        /\b(Android|Windows Phone|iPad|iPod)\b/i.test(UA);
    }
  }
  return hasTouchScreen;
}

function getEnvironment() {
  let hostName = window.location.hostname;
  if (
    hostName.indexOf("localhost") > -1 ||
    hostName.indexOf("dev.movember.com") > -1
  ) {
    return "local";
  } else if (hostName.indexOf("uat.movember.com") > -1) {
    return "uat";
  } else {
    return "produs";
  }
}

function getBaseUrl(country) {
  let baseCountry = country || "au";
  if (getEnvironment() === "local") {
    return `https://${baseCountry}.uat.movember.com/`;
  }
  return window.location.origin.concat("/");
}

function getAssetUrl(type, country, language) {
  let asset = null;
  let baseCountry = country || "au";
  switch (type) {
    default:
    case "cms":
      if (getEnvironment() === "local") {
        return `https://${baseCountry}.uat.movember.com/${
          language !== "en" ? language + "/" : ""
        }sitemap.json`;
      } else {
        return language !== "en"
          ? `/${language}/sitemap.json`
          : "/sitemap.json";
      }
    case "image":
      if (getEnvironment() === "local") {
        // Temporary link to image urls on uat
        asset = `https://${baseCountry}.uat.movember.com/uploads/images/resources/`;
      } else {
        asset = "/uploads/images/resources/";
      }
  }
  return asset;
}

function getConfigUrl(country) {
  let baseCountry = country || "au";
  if (getEnvironment() === "local") {
    return `https://${baseCountry}.uat.movember.com/resources/json/${baseCountry}/config.json`;
  } else {
    return `/resources/json/${baseCountry}/config.json`;
  }
}

/**
 * This was built initially to be able to render images from the legacy site, given the relative path
 * to the image on the legacy site, which will be appended to the legacy website domain matching the current
 * environment.
 *
 * @todo - handle locales (ie. https://ca.movember.com/fr/IMAGE)
 * @todo - auto-detect country if not specifically passed?
 *
 * @param type
 * @param path
 * @param country
 * @returns {*}
 */
function getLegacyAssetUrl(type, path, country) {
  let baseUrl = null;
  let baseCountry = country || "au"; // @todo, auto-detect default country?
  path = path.replace(/^\//, "");

  switch (type) {
    case "image":
      switch (getEnvironment()) {
        case "local":
          baseUrl = `https://${baseCountry}.uat.movember.com/`;
          break;
        case "produs":
          baseUrl = `https://${baseCountry}.movember.com/`;
          break;
        default:
          baseUrl =
            `https://${baseCountry}.` + getEnvironment() + `.movember.com/`;
          break;
      }
      break;
    case "api":
      baseUrl = `https://${
        baseCountry + (getEnvironment() === "produs" ? "" : ".uat")
      }.movember.com/api/v18/`;
      break;
    case "json":
      if (getEnvironment() === "local") {
        baseUrl = `https://${baseCountry}.uat.movember.com/`;
      } else {
        baseUrl = window.location.origin.concat("/");
      }
      break;
    default:
      baseUrl = window.location.origin.concat("/");
  }
  return baseUrl + path;
}

function getWordsPerMin(country) {
  switch (country) {
    case "au":
    case "us":
    case "uk":
    case "ca":
    case "nz":
      return 250;
    case "de":
      return 150;
    default:
      return 250;
  }
}

// Cookies get/set
function setCookie(cname, cvalue, exdays, hasDomain) {
  let d = new Date();
  d.setTime(d.getTime() + exdays * 24 * 60 * 60 * 1000);
  let expires = "expires=" + d.toUTCString();
  let domain = hasDomain
    ? `domain=${
        getEnvironment() === "dev"
          ? ".localhost:3000"
          : getEnvironment() === "uat"
          ? ".uat.movember.com;"
          : ".movember.com;"
      }`
    : "";
  document.cookie = cname + "=" + cvalue + ";" + domain + expires + ";path=/";
}

function getCookie(cname) {
  var name = cname + "=";
  var decodedCookie = decodeURIComponent(document.cookie);
  var ca = decodedCookie.split(";");
  for (var i = 0; i < ca.length; i++) {
    var c = ca[i];
    while (c.charAt(0) === " ") {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return "";
}

function getUserCookieName(postfix) {
  return `movember-${getEnvironment()}-2016_${postfix}`;
}

function getImage(item, size, isOriginal) {
  const prefix = getAssetUrl("image");
  let postfix = "";
  if (
    item &&
    item.id &&
    item.sizes &&
    item.sizes.length > 0 &&
    item.formats &&
    item.formats.length > 0
  ) {
    // IE doesn't support webp images
    // Don't use webp as original image
    let formatIndex = 0,
      sizeIndex = 0,
      webpIndex = item.formats.indexOf("webp");
    if (webpIndex >= 0) {
      if (isOriginal) {
        // get non-webp format index
        for (let i = 0; i < item.formats.length; i++) {
          if (i !== webpIndex) {
            formatIndex = i;
            break;
          }
        }
      } else {
        formatIndex = webpIndex;
      }
    }

    sizeIndex = item.sizes.indexOf(size) >= 0 ? item.sizes.indexOf(size) : 0;
    postfix = `${item.id}-${item.sizes[sizeIndex]}.${item.formats[formatIndex]}`;
  } else {
    return item ? item.url : null;
  }
  return prefix + postfix;
}


function transformConfig(inputConfig, countryCode, language) {


  let configs = {
    "country": countryCode,
    "language": language,
    "locale": {
        "au": {
          "language": [{ "code": "en", "name": "English" }]
        },
        "nz": {
          "language": [{ "code": "en", "name": "English" }]
        },
        "ca": {
          "language": [
            { "code": "en", "name": "English" },
            { "code": "fr", "name": "Français" }
          ]
        },
        "us": {
          "language": [{ "code": "en", "name": "English" }]
        },
        "uk": {
          "language": [{ "code": "en", "name": "English" }]
        },
        "ie": {
          "language": [{ "code": "en", "name": "English" }]
        }
      },
    "livechat": {
      "licenseId": inputConfig['app.tracking.livechat.code'],
      "enabled": ["au", "nz", "ca", "us", "uk"]
    },
    "cookie": {
      "enabled": [],
      "onetrust": {
        "scriptid": inputConfig['app.tracking.cookies.onetrust.scriptid'],

        "testing": inputConfig['pp.tracking.cookies.onetrust.testing'] === '1' ? true : false,
        "enabled": ["au", "nz", "ca", "us", "uk", "ie"]
      }
    },
    "facebook": {
      "appId": {
        "local": "10153428363195841",
        "uat": "",
        "produs": ""
      },
      "version": {
        "local": "v2.10",
        "uat": "",
        "produs": ""
      }
    }, 
    "app.site.donation.recurring.enabled": inputConfig['app.site.donation.recurring.enabled'] ? inputConfig['app.site.donation.recurring.enabled'] : '0'
  }

  if(inputConfig['app.tracking.livechat.enabled'] === '1') {
    configs.livechat.enabled = [countryCode];
  } else {
    configs.livechat.enabled = [];
  }

  if(inputConfig['app.tracking.cookies.onetrust.enabled'] === '1') {
    configs.cookie.onetrust.enabled = [countryCode];
  } else {
    configs.cookie.onetrust.enabled = [];
  }

  if (getEnvironment() === "uat") {
    configs.facebook.appId.uat = inputConfig['app.facebook_connect.appId'];
    configs.facebook.version.uat = inputConfig['app.facebook.sdk.version'];
  }

  if (getEnvironment() === "produs") {
    configs.facebook.appId.produs = inputConfig['app.facebook_connect.appId'];
    configs.facebook.version.produs = inputConfig['app.facebook.sdk.version'];
  }

  return configs;

}


function getUrlLocale() {
  let locale = {
    country: "au",
    language: "en",
  };
  let { pathname, hostname } = window.location;
  //Check if url cointains country and language code (ex: ca.movember.com/fr)
  if (hostname.charAt(2) === ".") {
    locale.country = hostname.substr(0, 2);
  }
  if (pathname.charAt(3) === "/") {
    locale.language = pathname.substr(1, 2);
  }
  return locale;
}

/**
 *
 * getUrlParam
 *
 * @param paramId the parameter name
 * @param additionalAllowedCharacters additional characters that we consider valid when looking for a parameter value in the URL.
 *                                    As a general rule, only letters, numbers, '_', '-', '+', '%' and '.' are valid.
 *                                    If we want to consider any other valid characters (e.g. '@'), we have to include them here.
 */
function getUrlParam(paramId, additionalAllowedCharacters) {
  var _chars = additionalAllowedCharacters || "";

  // Get the parameter string; note: for security reasons only certain characters are matched
  // eslint-disable-next-line
  var paramString = window.location.href.match(
    new RegExp(paramId + "=[-a-zA-Z0-9" + _chars + "_+%.]*", "g")
  ); // '_', '+', '%' and '.' characters added
  // Return the result:
  return paramString ? paramString[0].replace(paramId + "=", "") : null;
}

function getDateFormat(date, country) {
  switch (country) {
    case "us":
    case "ca":
      return date;
    default:
      break;
  }
  return date;
}

const copyToClipBoard = (value) => {
  if (navigator.userAgent.indexOf("Firefox") >= 0) {
    navigator.clipboard.writeText(value);
  } else {
    if (
      !document.queryCommandSupported ||
      !document.queryCommandSupported("copy")
    ) {
      return;
    }
    var clipboardContainer = document.getElementById("clipboard-container");
    setTimeout(function () {
      let input = document.createElement("input");
      input.setAttribute("readonly", "readonly");
      input.setAttribute("value", value);
      clipboardContainer.appendChild(input);

      input.select();
      input.setSelectionRange(0, input.value.length);

      document.execCommand("copy");
      clipboardContainer.removeChild(input);
    }, 500);
  }
};

function checkNested(obj, level1, ...deeperLevels) {
  if (!obj) return false;
  if ( deeperLevels.length === 0 && obj.hasOwnProperty(level1)) return true;
  return checkNested(obj[level1], ...deeperLevels);
}

function formatVideoUrl(url) {
  if (!url) return;
  if (url.indexOf('vimeo.com') >= 0) {
    return `${url.replace('vimeo.com', 'player.vimeo.com/video')}?autoplay=1`;
  }
  return `${url.replace('/watch?v=', '/embed/')}?autoplay=1`;
}

const scrollWithOffset = (event, el, offset = -180) => {
  event.preventDefault()
  const anchorPoint = document.getElementById(el)
  const yCoordinate = anchorPoint.getBoundingClientRect().top + window.scrollY;
  const yOffset = offset;
  window.scroll({ top: yCoordinate + yOffset});
}

export {
  useInterval,
  useWindowWidth,
  useComponentVisible,
  useClickOutside,
  shareModal,
  IsOnMobile,
  isTouchDevice,
  getEnvironment,
  setCookie,
  getCookie,
  getUserCookieName,
  renderParagraph,
  renderImage,
  transformConfig,
  getUrlLocale,
  getBaseUrl,
  getAssetUrl,
  getLegacyAssetUrl,
  getUrlParam,
  getDateFormat,
  getWordsPerMin,
  getImage,
  getConfigUrl,
  copyToClipBoard,
  checkNested,
  formatVideoUrl,
  scrollWithOffset
};
